/**
 * PTBFFixationFormDialog — Create / Edit PTBF Fixation (chốt giá).
 * Map theo SOP Table 57 (10 fields) + FR-PT-02..04 (SRS §2.9).
 *
 * Workflow:
 *  - Khi user chọn `contract_no` (autocomplete LocalP Differential), gọi
 *    `useGetLocalPQuery` để display CP Name + auto-fill `diffs` = `ptbf_diffs`,
 *    `quality_code` từ contract.
 *  - `final_price` = `market_level + diffs` (auto-compute, có toggle override).
 *  - BR-PT-1: cảnh báo (warn) nếu `total_lot < lot_fixed + lot_to_be_fixed`.
 *  - BR-PT-8: cảnh báo nếu `|final_price − (market_level + diffs)| > 0.01`.
 *  - Khi `is_upload=1` (đã issue) thì không cho sửa form (FR-PT-03/04).
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useGetLocalPQuery, useListLocalPQuery } from '@/store/api/localPApi'
import {
  useListFuturesMonthsQuery,
  useListQualitiesQuery,
} from '@/store/api/lookupsApi'
import {
  useCreatePTBFFixationMutation,
  useUpdatePTBFFixationMutation,
} from '@/store/api/ptbfFixationsApi'
import type {
  PTBFFixationCreateIn,
  PTBFFixationOut,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface PTBFFixationFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  /** Khi mở từ contract detail thì lock contract_no + ẩn autocomplete. */
  fixedContractNo?: string
  initialValue?: PTBFFixationOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface FixationFormValues {
  contract_no: string
  fixation_date: string
  market_level: number | ''
  cross_check_fix_level: string
  futures_month: string
  diffs: number | ''
  final_price: number | ''
  final_price_override: boolean
  total_lot: number | ''
  lot_fixed: number | ''
  lot_to_be_fixed: number | ''
  quality_code: string[]
  note: string
  is_upload: boolean
  issued_date: string
}

const EMPTY: FixationFormValues = {
  contract_no: '',
  fixation_date: dayjs().format('YYYY-MM-DD'),
  market_level: '',
  cross_check_fix_level: '',
  futures_month: '',
  diffs: '',
  final_price: '',
  final_price_override: false,
  total_lot: '',
  lot_fixed: '',
  lot_to_be_fixed: '',
  quality_code: [],
  note: '',
  is_upload: false,
  issued_date: '',
}

const schema: yup.ObjectSchema<FixationFormValues> = yup.object({
  contract_no: yup.string().trim().required('Bắt buộc chọn hợp đồng'),
  fixation_date: yup.string().required('Bắt buộc chọn ngày fixation'),
  market_level: yup
    .mixed<number | ''>()
    .test(
      'required',
      'Bắt buộc nhập Market Level',
      (v) => v !== '' && v !== undefined && v !== null,
    )
    .test('positive', 'Market Level phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  cross_check_fix_level: yup.string().trim().default(''),
  futures_month: yup.string().trim().default(''),
  diffs: yup.mixed<number | ''>().default(''),
  final_price: yup.mixed<number | ''>().default(''),
  final_price_override: yup.boolean().default(false),
  total_lot: yup.mixed<number | ''>().default(''),
  lot_fixed: yup.mixed<number | ''>().default(''),
  lot_to_be_fixed: yup.mixed<number | ''>().default(''),
  quality_code: yup.array().of(yup.string().required()).default([]),
  note: yup.string().trim().default(''),
  is_upload: yup.boolean().default(false),
  issued_date: yup.string().default(''),
}) as yup.ObjectSchema<FixationFormValues>

function fromFixation(f: PTBFFixationOut): FixationFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(f.contract_no),
    fixation_date: s(f.fixation_date),
    market_level: n(f.market_level),
    cross_check_fix_level: s(f.cross_check_fix_level),
    futures_month: s(f.futures_month),
    diffs: n(f.diffs),
    final_price: n(f.final_price),
    final_price_override: true, // edit mode: respect saved value
    total_lot: n(f.total_lot),
    lot_fixed: n(f.lot_fixed),
    lot_to_be_fixed: n(f.lot_to_be_fixed),
    quality_code: Array.isArray(f.quality_code) ? f.quality_code : [],
    note: s(f.note),
    is_upload: !!f.is_upload,
    issued_date: s(f.issued_date),
  }
}

function toApi(v: FixationFormValues): PTBFFixationCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: v.contract_no.trim(),
    fixation_date: v.fixation_date,
    market_level: typeof v.market_level === 'number' ? v.market_level : 0,
    futures_month: str(v.futures_month),
    total_lot: num(v.total_lot),
    lot_to_be_fixed: num(v.lot_to_be_fixed),
    lot_fixed: num(v.lot_fixed),
    diffs: num(v.diffs),
    final_price: num(v.final_price),
    cross_check_fix_level: str(v.cross_check_fix_level),
    quality_code: v.quality_code.length > 0 ? v.quality_code : null,
    note: str(v.note),
    is_upload: v.is_upload,
    issued_date: str(v.issued_date),
  }
}

export function PTBFFixationFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: PTBFFixationFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'
  const lockedByUpload = isEdit && !!initialValue?.is_upload

  const [createFixation, { isLoading: creating }] = useCreatePTBFFixationMutation()
  const [updateFixation, { isLoading: updating }] = useUpdatePTBFFixationMutation()
  const loading = creating || updating

  // Lookup options
  const { data: futuresMonths = [] } = useListFuturesMonthsQuery()
  const { data: qualityList = [] } = useListQualitiesQuery({ local_only: true })

  // Contracts autocomplete — Differential & active
  const { data: contractsList } = useListLocalPQuery({
    contract_type: 'Differential',
    is_status: 1,
    page: 1,
    page_size: 200,
  })
  const contractOptions = useMemo(
    () => contractsList?.items ?? [],
    [contractsList],
  )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FixationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const marketLevel = watch('market_level')
  const diffs = watch('diffs')
  const finalPrice = watch('final_price')
  const finalPriceOverride = watch('final_price_override')
  const totalLot = watch('total_lot')
  const lotFixed = watch('lot_fixed')
  const lotToBeFixed = watch('lot_to_be_fixed')

  // Fetch contract detail to display CP Name + auto-fill diffs/quality
  const { data: contractDetailRes } = useGetLocalPQuery(selectedContractNo, {
    skip: !selectedContractNo,
  })
  const contractDetail = contractDetailRes?.data

  const [lastFilledContractNo, setLastFilledContractNo] = useState('')

  useEffect(() => {
    if (!open) return
    setLastFilledContractNo('')
    if (isEdit && initialValue) {
      reset(fromFixation(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  // Auto-suggest diffs / quality_code when contract changes (create-mode only)
  useEffect(() => {
    if (isEdit) return
    if (!contractDetail) return
    if (!selectedContractNo) return
    if (lastFilledContractNo === selectedContractNo) return

    setValue('diffs', contractDetail.ptbf_diffs ?? '')
    setValue('futures_month', contractDetail.futures_month ?? '')
    // quality_code in LocalP is single string — wrap into array if present
    const qc = contractDetail.quality_code
    if (typeof qc === 'string' && qc.trim() !== '') {
      setValue('quality_code', [qc.trim()])
    }
    setLastFilledContractNo(selectedContractNo)
  }, [
    isEdit,
    contractDetail,
    selectedContractNo,
    lastFilledContractNo,
    setValue,
  ])

  // Auto-compute final_price = market_level + diffs (unless override)
  useEffect(() => {
    if (finalPriceOverride) return
    const ml = typeof marketLevel === 'number' ? marketLevel : null
    const df = typeof diffs === 'number' ? diffs : 0
    if (ml === null) return
    const fp = Number((ml + df).toFixed(4))
    setValue('final_price', fp)
  }, [marketLevel, diffs, finalPriceOverride, setValue])

  // ---- Warnings (BR-PT-1, BR-PT-8) — không block submit ----
  const lotWarning = useMemo(() => {
    const t = typeof totalLot === 'number' ? totalLot : null
    const f = typeof lotFixed === 'number' ? lotFixed : 0
    const tbf = typeof lotToBeFixed === 'number' ? lotToBeFixed : 0
    if (t === null) return null
    if (t < f + tbf) {
      return `BR-PT-1: Total Lot (${t}) < Lot Fixed + Lot To Be Fixed (${f + tbf}).`
    }
    return null
  }, [totalLot, lotFixed, lotToBeFixed])

  const finalPriceWarning = useMemo(() => {
    const ml = typeof marketLevel === 'number' ? marketLevel : null
    const df = typeof diffs === 'number' ? diffs : 0
    const fp = typeof finalPrice === 'number' ? finalPrice : null
    if (ml === null || fp === null) return null
    if (Math.abs(fp - (ml + df)) > 0.01) {
      return `BR-PT-8: Final Price (${fp}) lệch market_level + diffs (${(ml + df).toFixed(4)}) > 0.01.`
    }
    return null
  }, [marketLevel, diffs, finalPrice])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateFixation({
            id: initialValue.ptbf_p_id,
            body: payload,
          }).unwrap()
          snack.success('Đã cập nhật fixation')
          onSaved?.(res.data?.ptbf_p_id ?? initialValue.ptbf_p_id)
        } else {
          const res = await createFixation(payload).unwrap()
          snack.success('Đã tạo fixation')
          onSaved?.(res.data?.ptbf_p_id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(
            err,
            isEdit ? 'Cập nhật fixation thất bại' : 'Tạo fixation thất bại',
          ),
        )
      }
    },
    () => snack.error('Vui lòng kiểm tra lại các trường còn lỗi'),
  )

  const cpDisplay = useMemo(() => {
    if (!contractDetail) return ''
    return `${contractDetail.cp_code ?? ''} — ${contractDetail.supplier_name ?? contractDetail.short_name ?? ''}`
  }, [contractDetail])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? `Sửa PTBF Fixation #${initialValue?.ptbf_p_id ?? ''}` : 'Tạo PTBF Fixation'}
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {lockedByUpload && (
            <Alert severity="warning">
              Fixation đã được upload (issue) — không thể chỉnh sửa.
            </Alert>
          )}

          {/* Row 1: Contract + Fixation Date */}
          <Box display="flex" gap={2} flexWrap="wrap">
            {fixedContractNo ? (
              <TextField
                label="Hợp đồng"
                size="small"
                value={fixedContractNo}
                disabled
                sx={{ flex: '1 1 240px' }}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find(
                    (c) => c.contract_no === field.value,
                  )
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          supplier_name: null,
                          cp_code: null,
                        } as (typeof contractOptions)[number])
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      sx={{ flex: '2 1 320px' }}
                      options={contractOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt
                          ? `${opt.contract_no} — ${opt.supplier_name ?? opt.cp_code ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit || lockedByUpload}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Hợp đồng (Differential active) *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
            <Controller
              control={control}
              name="fixation_date"
              render={({ field }) => (
                <DatePicker
                  label="Ngày Fixation *"
                  format="DD-MMM-YYYY"
                  disabled={lockedByUpload}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 180px' },
                      error: !!errors.fixation_date,
                      helperText: errors.fixation_date?.message,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Row 2: CP Name display */}
          <TextField
            label="Nhà cung cấp"
            size="small"
            value={cpDisplay}
            disabled
            fullWidth
          />

          {/* Row 3: Quality Code (multi-select chips) */}
          <Controller
            control={control}
            name="quality_code"
            render={({ field }) => (
              <Autocomplete
                multiple
                size="small"
                disabled={lockedByUpload}
                options={qualityList.map((q) => q.code)}
                value={field.value}
                onChange={(_e, v) => field.onChange(v)}
                getOptionLabel={(opt) => {
                  const q = qualityList.find((x) => x.code === opt)
                  return q ? `${q.code} — ${q.description}` : opt
                }}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => {
                    const tagProps = getTagProps({ index: idx })
                    const { key, ...rest } = tagProps
                    return (
                      <Chip
                        key={key}
                        label={opt}
                        size="small"
                        {...rest}
                      />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Quality Code (multi-select)"
                    error={!!errors.quality_code}
                    helperText={
                      (errors.quality_code as { message?: string } | undefined)
                        ?.message
                    }
                  />
                )}
              />
            )}
          />

          {/* Row 4: Lots — Total / Fixed / To Be Fixed */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="total_lot"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Total Lot"
                  type="number"
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 140px' }}
                  error={!!errors.total_lot}
                  helperText={errors.total_lot?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="lot_fixed"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Lot Fixed"
                  type="number"
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 140px' }}
                  error={!!errors.lot_fixed}
                  helperText={errors.lot_fixed?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="lot_to_be_fixed"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Lot To Be Fixed"
                  type="number"
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 140px' }}
                  error={!!errors.lot_to_be_fixed}
                  helperText={errors.lot_to_be_fixed?.message}
                />
              )}
            />
          </Box>
          {lotWarning && <Alert severity="warning">{lotWarning}</Alert>}

          {/* Row 5: Market Level + Cross Check + Futures Month */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="market_level"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Market Level *"
                  type="number"
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 160px' }}
                  inputProps={{ step: '0.0001' }}
                  error={!!errors.market_level}
                  helperText={errors.market_level?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="cross_check_fix_level"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Cross Check Fix Level"
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 200px' }}
                  error={!!errors.cross_check_fix_level}
                  helperText={errors.cross_check_fix_level?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="futures_month"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Futures Month"
                  select
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 180px' }}
                  error={!!errors.futures_month}
                  helperText={errors.futures_month?.message}
                >
                  <MenuItem value="">— Chưa chọn —</MenuItem>
                  {futuresMonths.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          {/* Row 6: Diffs + Final Price + override */}
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Controller
              control={control}
              name="diffs"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Diffs"
                  type="number"
                  size="small"
                  disabled={lockedByUpload}
                  sx={{ flex: '1 1 140px' }}
                  inputProps={{ step: '0.0001' }}
                  helperText="Auto từ contract.ptbf_diffs"
                />
              )}
            />
            <Controller
              control={control}
              name="final_price"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Final Price"
                  type="number"
                  size="small"
                  disabled={lockedByUpload || !finalPriceOverride}
                  sx={{ flex: '1 1 160px' }}
                  inputProps={{ step: '0.0001' }}
                  helperText={
                    finalPriceOverride
                      ? 'Tự nhập (override)'
                      : '= market_level + diffs'
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="final_price_override"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      disabled={lockedByUpload}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Tự nhập Final Price"
                />
              )}
            />
          </Box>
          {finalPriceWarning && (
            <Alert severity="warning">{finalPriceWarning}</Alert>
          )}

          {/* Row 7: Issued Date + Is Uploaded */}
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Controller
              control={control}
              name="issued_date"
              render={({ field }) => (
                <DatePicker
                  label="Ngày phát hành"
                  format="DD-MMM-YYYY"
                  disabled={lockedByUpload}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 200px' },
                    },
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="is_upload"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      disabled={lockedByUpload}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Đã upload (issued)"
                />
              )}
            />
          </Box>

          {/* Row 8: Note */}
          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                label="Ghi chú"
                size="small"
                multiline
                minRows={2}
                disabled={lockedByUpload}
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading || lockedByUpload}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PTBFFixationFormDialog
